import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { API } from '../config';
import { marked } from 'marked';
import Navigation from '../components/Navigation';

export default function ArticleDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArticle();
  }, [slug]);

  const fetchArticle = async () => {
    try {
      const response = await fetch(`${API}/articles/slug/${slug}`);
      const data = await response.json();
      setArticle(data);
    } catch (error) {
      console.error('Failed to fetch article:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="pt-24 flex items-center justify-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="pt-24 text-center">
          <h2 className="text-2xl font-bold mb-4">Article not found</h2>
          <button 
            onClick={() => navigate('/articles')}
            className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Back to Articles
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      <article className="max-w-4xl mx-auto pt-24 pb-16 px-4">
        <button
          onClick={() => navigate('/articles')}
          className="flex items-center gap-2 text-blue-600 hover:text-blue-800 mb-8"
        >
          <ChevronLeft size={20} />
          Back to Articles
        </button>

        <div className="mb-8">
          <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium mb-4">
            {article.category}
          </span>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">
            {article.title}
          </h1>
          <div className="flex items-center gap-4 text-gray-600">
            <span>{article.author}</span>
            <span>•</span>
            <span>
              {article.created_at ? new Date(article.created_at).toLocaleDateString('en-US', { 
                month: 'long', 
                day: 'numeric', 
                year: 'numeric' 
              }) : 'Recent'}
            </span>
          </div>
        </div>

        <div 
          className="prose prose-lg max-w-none"
          style={{ color: '#1f2937' }}
          dangerouslySetInnerHTML={{ __html: marked.parse(article.content || '') }}
        />

        {article.tags && article.tags.length > 0 && (
          <div className="mt-12 pt-8 border-t">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Tags</h3>
            <div className="flex flex-wrap gap-2">
              {article.tags.map(tag => (
                <span key={tag} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
      </article>
    </div>
  );
}
